import EmptyMessage from '@/components/EmptyMessage/EmptyMessage';
import DOMPurify from 'dompurify';
import Loader, { Status } from '@/components/Loader/Loader';
import Modal from '@/components/Modal/Modal';
import { UserRole, type User } from '@/modules/user/userTypes';

import {
	CloseThin,
	Plus,
	InfoGold,
	TrashFullSquare,
	CheckMarkGreen,
	SearchIconGold,
	FourCircles,
	MapPin,
	Coin,
	Hashtag,
	LogoText,
	InfoCircle,
	Renewal
} from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';
import classNames from 'classnames';
import { fromJS } from 'immutable';
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import {
	dashboardGetAllBusinesses,
	dashboardGetBusinessesRequest
} from '@/modules/dashboard/dashboardActions';
import map from '@/assets/images/map.png';
import { normalizeHTML } from '@/utils/normalizeHTML';
import type { FilterProps } from '@/components/molecules/Filters';
import type {
	Business,
	BusinessQuery,
	List
} from '../../dashboard/dashboardTypes';
import EmailForm from '../components/EmailForm/EmailForm';
import EmailList from '../components/EmailList/EmailList';
import EmailTemplateList from '../components/EmailTemplateList/EmailTemplateList';
import EmailSmartTemplates from '../components/EmailSmartTemplates/EmailSmartTemplates';
import EmailSmartTemplatesRedesign from '../components/EmailSmartTemplates/EmailSmartTemplatesRedesign';

import {
	emailCreateEmailTemplateRequest,
	emailDeleteEmailTemplateRequest,
	emailResetEmail,
	emailUpdateEmail,
	emailUpdateEmailTemplateRequest,
	emailClearEmailContactedStatus,
	emailGetEmailStatuses,
	emailClearEmailQueue,
	emailCancelScheduledEmail,
	emailCancelScheduledEmailClearStatus,
	emailGetScheduledEmailLimits,
	emailClearUnstaledTemplate,
	emailClearLastStaledTemplate,
	emailGetAICredits,
	emailDeleteAISnippets,
	emailRevertAITemplate,
	emailGetMonthlyEmailLimit
} from '../emailActions';
import {
	DirectEmail,
	Email,
	EmailStatus,
	EmailTemplate,
	EMAIL_TEMPLATE_AUTO_SAVE_TIMEOUT
} from '../emailTypes';
import './EmailModal.scss';
import SendAllModal from '../components/SendAllModal/SendAllModal';
import Toaster from './Toaster';

type EmailModalProps = FilterProps & {
	onClose: () => void;
	show: boolean;
	showPreview?: boolean;
	setShowPreview?: (e: boolean) => void;
	business?: Business;
	directEmail?: DirectEmail;
	linkedInSharer: (e: React.MouseEvent) => void;
	setDeleteDataIndex?: (x: number) => void;
	sendAllMode?: boolean;
	setSendAllMode?: (x: boolean) => void;
	listId?: List['id'];
};

export default function EmailModal({
	onClose,
	show = false,
	showPreview = false,
	setShowPreview,
	business,
	linkedInSharer,
	directEmail,
	setDeleteDataIndex,
	sendAllMode,
	setSendAllMode,
	listId,
	appliedFilters,
	onFilterChange
}: EmailModalProps): JSX.Element {
	const [
		user,
		businesses,
		allBusinesses,
		selectedBusiness,
		addBusinessDataSuccess,
		deleteBusinessDataSuccess,
		emailSubject,
		emailBody,
		emailFailure,
		emailQueue,
		monthlyEmailLimit,
		emailTemplates,
		emailTemplatesLoading,
		emailTemplatesAutoSaveLoading,
		emailTemplatesAutoSaveSuccess,
		createEmailTemplateSucceed,
		deleteEmailTemplateSucceed,
		staleTemplates,
		unstaledTemplate,
		lastStaledTemplate,
		list,
		scheduledEmailActivity,
		cancelScheduledEmailLoading,
		cancelScheduledEmailSuccess,
		cancelScheduledEmailFailure,
		aiFailure,
		aiCredits,
		aiLoading,
		aiText,
		aiTemplates
	] = useSelector<
		RootState,
		[
			User,
			Business[],
			Business[],
			Business,
			boolean,
			boolean,
			string,
			any,
			string,
			any,
			number,
			EmailTemplate[],
			boolean,
			boolean,
			boolean,
			boolean,
			boolean,
			any,
			EmailTemplate,
			EmailTemplate,
			List,
			boolean,
			any,
			boolean,
			boolean,
			boolean,
			number,
			boolean,
			any,
			any
		]
	>(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.dashboard.getIn(['businesses', 'data']).toJS(),
			state.dashboard.getIn(['businesses', 'allData']).toJS(),
			state.dashboard.getIn(['businesses', 'selected']).toJS(),
			state.dashboard.getIn(['businesses', 'addedDataSuccess']),
			state.dashboard.getIn(['businesses', 'deletedDataSuccess']),
			state.email.getIn(['email', 'subject']),
			state.email.getIn(['email', 'body']),
			state.email.getIn(['email', 'failure']),
			state.email.getIn(['email', 'queue']).toJS(),
			state.email.getIn(['email', 'monthlyLimit']),
			state.email.getIn(['templates', 'data']).toJS(),
			state.email.getIn(['templates', 'isLoading']),
			state.email.getIn(['templates', 'autoSaveLoading']),
			state.email.getIn(['templates', 'autoSaveSuccess']),
			state.email.getIn(['templates', 'createSucceed']),
			state.email.getIn(['templates', 'deleteSucceed']),
			state.email.getIn(['templates', 'stale']).toJS(),
			state.email.getIn(['templates', 'unstaledTemplate']).toJS(),
			state.email.getIn(['templates', 'lastStaledTemplate']).toJS(),
			state.dashboard.getIn(['lists', 'selected']).toJS(),
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
			state.email.getIn(['email', 'scheduled', 'cancel', 'loading']),
			state.email.getIn(['email', 'scheduled', 'cancel', 'success']),
			state.email.getIn(['email', 'scheduled', 'cancel', 'failure']),
			state.email.getIn(['ai', 'failure']),
			state.email.getIn(['ai', 'credits']),
			state.email.getIn(['ai', 'loading']),
			state.email.getIn(['ai', 'text']).toJS(),
			state.email.getIn(['ai', 'templates']).toJS()
		],
		shallowEqual
	);

	const dispatch = useDispatch();
	const updateEmail = (payload: Email) => dispatch(emailUpdateEmail(payload));
	const resetEmail = () => dispatch(emailResetEmail());

	const getBusinesses = useCallback(
		({ skip = 0, limit = 50, ...filters }: BusinessQuery) => {
			dispatch(
				dashboardGetBusinessesRequest({
					listId,
					skip,
					limit,
					...filters,
					count: true
				})
			);
		},
		[dispatch, listId]
	);

	const createEmailTemplate = (payload: EmailTemplate) =>
		dispatch(emailCreateEmailTemplateRequest(payload));
	const updateEmailTemplate = (payload: EmailTemplate) =>
		dispatch(emailUpdateEmailTemplateRequest(payload));
	const deleteEmailTemplate = (templateId: string) =>
		dispatch(emailDeleteEmailTemplateRequest({ id: templateId }));

	const clearEmailContactedStatus = () =>
		dispatch(emailClearEmailContactedStatus());
	const getEmailStatuses = () => dispatch(emailGetEmailStatuses());
	const clearEmailQueue = () => dispatch(emailClearEmailQueue());
	const getScheduledEmailLimits = () =>
		dispatch(emailGetScheduledEmailLimits());
	const cancelScheduledEmail = (businessId: string, scheduleDate: string) =>
		dispatch(
			emailCancelScheduledEmail({ businessId, listId, scheduleDate })
		);
	const cancelScheduledEmailClearStatus = () =>
		dispatch(emailCancelScheduledEmailClearStatus());
	const clearUnstaledTemplate = () => dispatch(emailClearUnstaledTemplate());
	const clearLastStaledTemplate = () =>
		dispatch(emailClearLastStaledTemplate());
	const getAICredits = () => dispatch(emailGetAICredits());
	const getAllBusinesses = () => dispatch(dashboardGetAllBusinesses(listId));
	const deleteAISnippets = (templateId: string) =>
		dispatch(emailDeleteAISnippets({ listId, templateId }));
	const revertAITemplate = () => dispatch(emailRevertAITemplate());
	const getMonthlyEmailLimit = () => dispatch(emailGetMonthlyEmailLimit());

	const [removedEmails, setRemovedEmails] = useState<string[]>([]);
	const [businessEmails, setBusinessEmails] = useState<Business[]>([]);
	const [allBusinessEmails, setAllBusinessEmails] = useState<Business[]>([]);
	const [activeBusiness, setActiveBusiness] = useState<Business>();
	const [showTemplates, setShowTemplates] = useState(true);
	const [showCreate, setShowCreate] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>();
	const [autoSaveStatus, setAutoSaveStatus] = useState<Status>(Status.NONE);
	const [deleteTemplateId, setDeleteTemplateId] = useState<string>('');
	const [initialRender, setInitialRender] = useState<boolean>(false);
	const [customEmailModalDisplay, setCustomEmailModalDisplay] =
		useState<boolean>(false);
	const [showAiCreditWarning, setShowAiCreditWarning] =
		useState<boolean>(false);
	const [showAiTemplateWarning, setShowAiTemplateWarning] =
		useState<boolean>(false);
	const [showAiLoadingModal, setShowAiLoadingModal] =
		useState<boolean>(false);

	const aiLoadingTips = [
		'Creating smaller lists and focusing on one use case at a time will yield better results.',
		'Personalize your emails to your customers. You know your customers better than anyone, so we recommend adding or editing content based on your expertise.',
		"Make your sales proposition clear in the first paragraph, so your new potential customers know why you're writing.",
		"Avoid spam words that can affect your domain reputation. Words like 'opportunity, bargain, cheap' should be avoided.",
		'Use your own voice! The best prospect message is the one that sounds like you. So, be yourself, and write as if you were talking to the prospect face-to-face.',
		"Schedule your emails to your customers schedule. You are the expert of your customers, so schedule your emails for when they're most active."
	];

	const nextMonth = new Date(
		new Date().getFullYear(),
		new Date().getMonth() + 1,
		1
	).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	});

	const [currentAiLoadingTip, setCurrentAiLoadingTip] = useState<string>(
		aiLoadingTips[0]
	);

	const rotateTipsIntervalId = useRef<ReturnType<typeof setInterval>>();

	const rotateTips = () => {
		let currentIndex = 1;

		rotateTipsIntervalId.current = setInterval(() => {
			setCurrentAiLoadingTip(aiLoadingTips[currentIndex]);
			currentIndex = (currentIndex + 1) % aiLoadingTips.length;
		}, 6000);
	};

	useEffect(() => {
		getAICredits();

		if (listId) {
			if (!directEmail) {
				getAllBusinesses();
			}

			getBusinesses({
				skip: 0,
				limit: 50,
				listId
			});
		}

		if (!monthlyEmailLimit) {
			getMonthlyEmailLimit();
		}
	}, []);

	useEffect(() => {
		if (!isEmpty(emailQueue)) {
			clearEmailQueue();
		}

		getScheduledEmailLimits();
	}, []);

	const [templatesList, setTemplatesList] = useState<EmailTemplate[]>();
	const [isVisible, setIsVisible] = useState(false); // New state for visibility

	const [showToaster, setShowToaster] = useState(false);
	const [toasterMessage, setToasterMessage] = useState('');
	const toasterRef = useRef<HTMLDivElement>(null);
	const handleClickOutside = (event: MouseEvent) => {
		if (
			toasterRef.current &&
			!toasterRef.current.contains(event.target as Node)
		) {
			setIsVisible(false); // Start fade out
			setTimeout(() => {
				setShowToaster(false);
			}, 500); // Match fade-out animation duration
		}
	};

	useEffect(() => {
		// Event listener for clicks outside the toaster
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const cancelScheduledEmailButtonText = () => {
		if (cancelScheduledEmailLoading) {
			return 'Cancelling';
		}

		if (cancelScheduledEmailSuccess) {
			return 'Cancelled';
		}

		return 'Cancel scheduled';
	};

	useEffect(() => {
		if (
			selectedTemplate &&
			createEmailTemplateSucceed &&
			!selectedTemplate?.id &&
			emailTemplates &&
			emailTemplates.length > 0
		) {
			setSelectedTemplate({
				...selectedTemplate,
				id: emailTemplates[0].id
			});
		}
	}, [emailTemplates]);

	useEffect(() => {
		if (!isEmpty(unstaledTemplate) && initialRender) {
			setSelectedTemplate(unstaledTemplate);
			setTemplatesList(emailTemplates);
		}
	}, [unstaledTemplate?.id]);

	useEffect(() => {
		if (!isEmpty(lastStaledTemplate) && initialRender) {
			setSelectedTemplate(lastStaledTemplate);
		}
	}, [lastStaledTemplate?.id]);

	useEffect(() => {
		setInitialRender(true);
	}, []);

	const continueToEmailsAnalytic = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Clicked Continue to Emails'
			});
		}
	};

	const generateTemplateName = (templates: EmailTemplate[]): string => {
		const baseName = 'New Template';
		const existingNames = templates.map(template => template.name);

		// Filter names that match 'New Template #' pattern and extract numbers
		const numbersOfDefaultNames = templates
			.filter(template => template.name?.startsWith(`${baseName} #`))
			.map(template =>
				parseInt(template.name.replace(`${baseName} #`, ''), 10)
			)
			.filter(number => !isNaN(number)); // Ensure we only have valid numbers

		// If 'New Template #' names exist, determine the next available number
		if (numbersOfDefaultNames.length) {
			const index = Math.max(...numbersOfDefaultNames) + 1;
			return `${baseName} #${index}`;
		}

		// If no 'New Template #' names exist, check if 'New Template' exists
		if (!existingNames.includes(baseName)) {
			return baseName;
		}

		// If 'New Template' exists, start numbering from #2
		return `${baseName} #2`;
	};

	const emailDebounce = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		if (emailTemplatesAutoSaveLoading) {
			setAutoSaveStatus(Status.LOADING);
		}
	}, [emailTemplatesAutoSaveLoading]);

	useEffect(() => {
		if (emailTemplatesAutoSaveSuccess) {
			setAutoSaveStatus(Status.SUCCESS);
			setTemplatesList(emailTemplates);
		}
	}, [emailTemplatesAutoSaveSuccess]);

	useEffect(() => {
		if (autoSaveStatus === Status.SUCCESS) {
			setAutoSaveStatus(Status.NONE);
		}
	}, [selectedTemplate?.id]);

	useEffect(() => {
		if (createEmailTemplateSucceed && initialRender) {
			setTemplatesList(emailTemplates);
		}
	}, [createEmailTemplateSucceed]);

	useEffect(() => {
		if (deleteEmailTemplateSucceed && initialRender) {
			setTemplatesList(emailTemplates);
		}
	}, [deleteEmailTemplateSucceed]);

	useEffect(() => {
		if (selectedTemplate && selectedTemplate.defaultTemplate) {
			setHasCreatedNewTemplate(false); // Reset when switching back to default template
			setShowToaster(false);
			setIsVisible(false); // Show toaster
		}
	}, [selectedTemplate]);

	const validEmails = (businessList: Business[]) => {
		return businessList.filter((b: Business) => {
			let businessEmail = '';
			let emailCount = 0;
			let lowPriorityCount = 0;

			if (b?.email && b?.email.length > 0) {
				businessEmail = String(b?.email).toLowerCase();
				emailCount++;

				if (b?.emailLowPriority) {
					lowPriorityCount++;
				}
			}

			if (b?.facebook?.email && b?.facebook?.email.length > 0) {
				businessEmail = String(b?.facebook?.email).toLowerCase();
				emailCount++;

				if (b?.facebook?.emailLowPriority) {
					lowPriorityCount++;
				}
			}

			if (b?.web?.email && b?.web?.email.length > 0) {
				businessEmail = String(b?.web?.email).toLowerCase();
				emailCount++;

				if (b?.web?.emailLowPriority) {
					lowPriorityCount++;
				}
			}

			if (b?.userAddedData && b?.userAddedData.length > 0) {
				for (let i = 0; i < b.userAddedData.length; i++) {
					if (b.userAddedData[i].type === 'email') {
						businessEmail = String(
							b.userAddedData[i].data
						).toLowerCase();
						emailCount++;

						if (b.userAddedData[i]?.emailLowPriority) {
							lowPriorityCount++;
						}
					}
				}
			}

			if (businessEmail.length === 0) {
				return false;
			}

			if (emailCount === lowPriorityCount) {
				return false;
			}

			if (removedEmails.indexOf(String(b._id).trim()) !== -1) {
				return false;
			}
			return true;
		});
	};

	const filterHiddenBusinesses = () => {
		const newBusinessEmails = validEmails(businesses);

		if (!fromJS(newBusinessEmails).equals(fromJS(businessEmails))) {
			setBusinessEmails(newBusinessEmails);

			if (
				removedEmails[removedEmails.length - 1] ===
					activeBusiness?._id ||
				removedEmails.length === 0
			) {
				setActiveBusiness(newBusinessEmails[0]);
			}
		}
	};

	const filterAllHiddenBusinesses = () => {
		const newBusinessEmails = validEmails(allBusinesses);
		setAllBusinessEmails(newBusinessEmails);
	};

	useEffect(() => {
		filterAllHiddenBusinesses();
	}, [allBusinesses.length]);

	useEffect(() => {
		if (!directEmail) {
			filterHiddenBusinesses();
			filterAllHiddenBusinesses();
		}
	}, [removedEmails, businesses[0]?.name, directEmail]);

	useEffect(() => {
		if (addBusinessDataSuccess) {
			filterHiddenBusinesses();
			filterAllHiddenBusinesses();
			setActiveBusiness(selectedBusiness);
		}
	}, [addBusinessDataSuccess]);

	useEffect(() => {
		if (deleteBusinessDataSuccess) {
			filterHiddenBusinesses();
			filterAllHiddenBusinesses();
			setActiveBusiness(selectedBusiness);
		}
	}, [deleteBusinessDataSuccess]);

	useEffect(() => {
		if (show) {
			setRemovedEmails([]);
		}
	}, [show]);

	useEffect(() => {
		if (directEmail) {
			setActiveBusiness(directEmail.business);
			setBusinessEmails([directEmail.business]);
		}
	}, [directEmail]);

	const openDeleteTemplateModal = (id: string) => {
		const e = document.getElementById('delete-template-modal');
		e?.classList.remove('delete-template-modal-fade');
		setTimeout(() => {
			e?.classList.remove('delete-template-modal-closed');
		}, 500);

		setDeleteTemplateId(id);
	};

	const closeModal = (className: string) => {
		const e = document.getElementById(className);
		e?.classList.add(`${className}-fade`);
		setTimeout(() => {
			e?.classList.add(`${className}-closed`);
		}, 500);
	};

	const openModal = (className: string) => {
		const e = document.getElementById(className);
		e?.classList.remove(`${className}-fade`);
		setTimeout(() => {
			e?.classList.remove(`${className}-closed`);
		}, 500);
	};

	const openScheduledSendCancelModal = () => {
		const e = document.getElementById('scheduled-send-cancel-modal');
		e?.classList.remove('scheduled-send-cancel-modal-fade');
		setTimeout(() => {
			e?.classList.remove('scheduled-send-cancel-modal-closed');
		}, 500);
	};

	const closeScheduledSendCancelModal = () => {
		const e = document.getElementById('scheduled-send-cancel-modal');
		e?.classList.add('scheduled-send-cancel-modal-fade');
		setTimeout(() => {
			e?.classList.add('scheduled-send-cancel-modal-closed');
		}, 500);

		cancelScheduledEmailClearStatus();
	};

	useEffect(() => {
		if (selectedTemplate && staleTemplates[`${selectedTemplate?.id}`]) {
			openModal('stale-template-modal');
		}
	}, [Object.keys(staleTemplates).length]);

	useEffect(() => {
		if (cancelScheduledEmailSuccess) {
			setTimeout(() => {
				closeScheduledSendCancelModal();

				if (setShowPreview && showPreview) {
					setShowPreview(false);
				}
			}, 1000);
		}
	}, [cancelScheduledEmailSuccess]);

	const resetClose = () => {
		resetEmail();
		setShowAiLoadingModal(false);
		onClose();
	};

	const setShowCreateAnalytic = () => {
		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Created an Email Template'
			});
		}
		setShowCreate(true);
	};

	useEffect(() => {
		if (emailTemplates && emailTemplates.length && !templatesList) {
			setTemplatesList(emailTemplates);
		}
	}, [emailTemplates]);

	const wordFilterTemplates = searchVal => {
		const newArr = [];
		emailTemplates.forEach(val => {
			if (val.name.toLowerCase().includes(searchVal.toLowerCase())) {
				newArr.push(val);
			}
		});
		setTemplatesList(newArr);
	};

	const [showSendAll, setShowSendAll] = useState<boolean>(false);
	const [hasCreatedNewTemplate, setHasCreatedNewTemplate] = useState(false);

	const getUniqueTemplateName = (
		name: string,
		emailTemplates: EmailTemplate[]
	) => {
		let counter = 1;
		let uniqueName = name;

		// Check if the exact name exists
		if (
			emailTemplates.some(
				template =>
					template.name === uniqueName &&
					template.id !== selectedTemplate?.id
			)
		) {
			uniqueName = `${name} copy`; // First duplicate uses "copy"
		}

		// Loop to ensure uniqueness, adding incremented numbers if needed
		while (
			emailTemplates.some(
				template =>
					template.name === uniqueName &&
					template.id !== selectedTemplate?.id
			)
		) {
			counter++;
			uniqueName = `${name} copy ${counter}`; // Increment number for additional duplicates
		}

		return uniqueName;
	};

	const emailOnChange = (
		subject: string,
		body: string,
		to: string,
		bcc?: string,
		name?: string
	) => {
		const newTemplateName = getUniqueTemplateName(
			name || selectedTemplate?.name,
			emailTemplates // Pass emailTemplates to check for name conflicts
		);

		// Check if there is a change in each field
		const subjectChanged = selectedTemplate?.subject !== subject;
		const bodyChanged = selectedTemplate?.body !== body;
		const nameChanged = name && selectedTemplate?.name !== name;

		// Trigger loading status immediately on change
		if (subjectChanged || bodyChanged || nameChanged) {
			setAutoSaveStatus(Status.LOADING);
		}

		// Debounce timeout for saving the template
		if (emailDebounce.current) {
			clearTimeout(emailDebounce.current);
		}

		// Debounced save action
		emailDebounce.current = setTimeout(() => {
			if (selectedTemplate && selectedTemplate.defaultTemplate) {
				// If any field has changed, but skip toaster if only the name has changed
				if (subjectChanged || bodyChanged || nameChanged) {
					if (!hasCreatedNewTemplate) {
						// Create a new template based on the current edits
						const newTemplate = {
							...selectedTemplate,
							defaultTemplate: false,
							id: undefined, // Clear the id to signify it's a new template
							subject,
							body,
							name: newTemplateName // Use the new name or preserve the old one
						};

						// Show the toaster only if subject or body has changed
						if ((subjectChanged || bodyChanged) && !showToaster) {
							setShowToaster(true);
							setTimeout(() => setIsVisible(true), 300); // Adjust delay as needed
							setToasterMessage(
								'Your edits have been saved as a new template. Feel free to rename it to distinguish it from the original default template.'
							);
						}

						// Dispatch the create template action
						createEmailTemplate(newTemplate);

						// Set the flag to true, indicating a new template has been created
						setHasCreatedNewTemplate(true);
					} else {
						// If the new template has already been created, just update the fields
						const updatedTemplate = {
							...selectedTemplate,
							subject,
							body: normalizeHTML(body),
							name: newTemplateName
						};

						// Dispatch the update action for the existing template
						updateEmailTemplate(updatedTemplate);
					}
				}
			} else if (
				selectedTemplate &&
				(subjectChanged || bodyChanged || nameChanged)
			) {
				// If it's not a default template, just update the current selected template
				const updatedTemplate = {
					...selectedTemplate,
					subject,
					body: normalizeHTML(body),
					name: newTemplateName
				};

				// Dispatch the update action for the existing template
				updateEmailTemplate(updatedTemplate);
			}
			if (
				(subjectChanged || bodyChanged || nameChanged) &&
				emailTemplatesAutoSaveSuccess
			) {
				setTimeout(() => setAutoSaveStatus(Status.SUCCESS), 300); // Prevents save fade out from debounce
			}
		}, EMAIL_TEMPLATE_AUTO_SAVE_TIMEOUT);

		// Update selectedTemplate state optimistically to reflect the change immediately
		if (selectedTemplate) {
			setSelectedTemplate({ ...selectedTemplate, subject, body, name });
		}
	};

	const classicMode = () => {
		updateEmail(selectedTemplate as Email);
		setShowTemplates(false);
		continueToEmailsAnalytic();
		clearUnstaledTemplate();
		clearLastStaledTemplate();

		const el = document.querySelector('.modali');
		el?.classList.remove('hide');

		if (autoSaveStatus === Status.SUCCESS) {
			setAutoSaveStatus(Status.NONE);
		}
	};

	const backToTemplateEdit = () => {
		setShowTemplates(true);
		clearUnstaledTemplate();
	};

	useEffect(() => {
		if (showCreate) {
			const name = generateTemplateName(emailTemplates);
			const template = { name, subject: emailSubject, body: emailBody };
			setSelectedTemplate(template);
			createEmailTemplate(template);
		}
	}, [showCreate]);

	const openAiFailureModal = () => {
		const modalEl = document.getElementsByClassName('ai-failure-outer')[0];
		if (modalEl) {
			modalEl.classList.add('ai-failure-outer-exists');
			setTimeout(() => {
				modalEl.classList.add('ai-failure-outer-visible');
			}, 50);
		}
	};

	const closeAiFailureModal = () => {
		const modalEl = document.getElementsByClassName('ai-failure-outer')[0];
		if (modalEl) {
			modalEl.classList.remove('ai-failure-outer-visible');
			setTimeout(() => {
				modalEl.classList.remove('ai-failure-outer-exists');
			}, 400);
		}
	};

	const handleClearFilters = useCallback(() => {
		const newFilters = {
			limit: 50,
			skip: 0,
			count: true
		};

		onFilterChange(newFilters);
		getBusinesses(newFilters);
	}, [getBusinesses, onFilterChange]);

	const closeEmailModal = () => {
		clearEmailContactedStatus();
		clearUnstaledTemplate();
		setInitialRender(false);
		setHasCreatedNewTemplate(false);
		handleClearFilters();
		if (!isEmpty(emailQueue)) {
			getEmailStatuses();
		}

		const shouldSetQueryTypeTo2 =
			appliedFilters?.revertIncludeEmailsYes && appliedFilters?.listId;

		const newFilters = {
			limit: 50,
			...appliedFilters,
			skip: 0,
			count: appliedFilters?.revertIncludeEmailsYes
				? true
				: appliedFilters?.count,
			queryType: shouldSetQueryTypeTo2 ? 2 : appliedFilters?.queryType,
			includeEmailsYes: undefined,
			revertIncludeEmailsYes: undefined
		};

		onFilterChange(newFilters);
		getBusinesses(newFilters);
	};

	useEffect(() => {
		if (!aiLoading) {
			setShowAiLoadingModal(false);
			clearInterval(rotateTipsIntervalId.current);
		}
	}, [aiLoading]);

	useEffect(() => {
		if (aiFailure) {
			openAiFailureModal();
		}
	}, [aiFailure]);

	let buttons: ReactNode[] = [];
	let content: ReactNode = null;
	let title: ReactNode | string = 'Edit Template';
	let closeIcon: ReactNode = (
		<CloseThin
			width={18}
			height={18}
			className="text-white"
			onClick={() => {
				closeEmailModal();
			}}
		/>
	);

	let modalSize = 'lg';
	let modalHeight = 'auto';
	let bodyClass = '';

	if (showPreview && business) {
		title = (
			<span className="emailModal-preview--title testModal flex items-center justify-between pr-7 text-white">
				<span>
					Email{' '}
					{(business.emailStatus === EmailStatus.Waiting ||
						business.emailStatus === EmailStatus.Sent) &&
					scheduledEmailActivity[business._id]
						? `${EmailStatus.Sent} to`
						: ''}{' '}
					{scheduledEmailActivity[business._id] &&
						'scheduled to send to'}
					{business.emailStatus === EmailStatus.Open
						? 'Opened by'
						: ''}{' '}
					{String(business.name)}
					{scheduledEmailActivity[business._id] && (
						<>
							{' '}
							on
							<span className="emailModal-preview--scheduled-date">
								{` ${dayjs(
									scheduledEmailActivity[business._id]
										.scheduleDate,
									'MM/DD/YYYY, h:mm:ss A'
								)?.format('ddd, MMM D')} at ${dayjs(
									scheduledEmailActivity[business._id]
										.scheduleDate,
									'MM/DD/YYYY, h:mm:ss A'
								)?.format('hh:mm a')}`}
							</span>
						</>
					)}
				</span>
			</span>
		);

		buttons = [];

		content = (
			<div className="flex flex-col pt-2">
				<div className="mt-2">
					<span className="text-white">Subject:</span>
					<p className="emailModal-preview--content mt-2 w-full rounded p-2 px-4 text-black">
						{business?.scheduledEmailSubject ??
							business.emailSubject}
					</p>
				</div>

				<div className="mt-4">
					<span className="text-white">Message:</span>
					<p
						className="emailModal-preview--content mt-2 w-full rounded p-4 text-black"
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(
								business?.scheduledEmailBody ??
									business.emailBody
							)
						}}
					/>
				</div>
				{scheduledEmailActivity[business._id] && (
					<div className="emailModal-preview-button-holder">
						<button
							className="scheduled-send-cancel-button"
							onClick={openScheduledSendCancelModal}
						>
							<TrashFullSquare className="scheduled-send-cancel-icon" />
							Cancel scheduled
						</button>
					</div>
				)}
			</div>
		);
	} else if (show) {
		if (showTemplates) {
			if (selectedTemplate) {
				modalSize = 'xl';
				bodyClass = 'px-4';
				modalHeight = 'full';

				buttons = [
					<button
						type="button"
						className="emailModal-back-button"
						onClick={() => {
							setSelectedTemplate(undefined);
							setShowCreate(false);
							clearUnstaledTemplate();
						}}
					>
						Go back
					</button>,
					<button
						type="button"
						className="emailModal-continue-button"
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();

							if (
								aiCredits !== undefined &&
								aiCredits > 0 &&
								aiCredits < list?.businessIds?.length &&
								selectedTemplate.body?.includes(
									'[ai_snippet]'
								) &&
								!aiText?.[`${list.id}`]?.[
									`${selectedTemplate?.id}`
								]
							) {
								setShowAiCreditWarning(true);
								return;
							}

							if (
								selectedTemplate.body?.includes(
									'[ai_snippet]'
								) &&
								Object.keys(
									aiText?.[`${list.id}`]?.[
										`${selectedTemplate?.id}`
									] ?? {}
								).length < allBusinessEmails?.length &&
								aiCredits !== undefined &&
								aiCredits > 0 &&
								selectedTemplate.body?.includes(
									'[ai_snippet]'
								) &&
								aiTemplates?.[`${selectedTemplate?.id}`]?.id ===
									selectedTemplate.id &&
								aiTemplates?.[`${selectedTemplate?.id}`]
									?.body !== selectedTemplate.body
							) {
								setShowAiTemplateWarning(true);
								return;
							}

							if (
								selectedTemplate?.id &&
								selectedTemplate.body?.includes(
									'[ai_snippet]'
								) &&
								aiCredits > 0 &&
								!aiLoading &&
								Object.keys(
									aiText?.[list.id]?.[selectedTemplate?.id] ??
										{}
								).length < list?.businessIds?.length
							) {
								setShowAiLoadingModal(true);
								rotateTips();
							}

							if (sendAllMode) {
								setShowSendAll(true);
								const el = document.querySelector('.modali');
								el?.classList.add('hide');
							} else {
								classicMode();
							}
						}}
					>
						Continue
					</button>
				];
				content = (
					<div className="edit-template-holder relative flex h-full flex-grow flex-col">
						<div className="mb-4 mt-3 flex items-center justify-between">
							<span className="text-2xl font-light text-white">
								{/* Edit Template */}
							</span>
							{(selectedTemplate?.isStale ||
								staleTemplates?.[
									`${selectedTemplate?.id}`
								]) && (
								<p className="edit-template-stale-warning">
									If an email template has been used more than
									500 times it will be marked as "stale" and
									can no longer be used.
									<br />
									In order to use this template again, you
									need to change{' '}
									<span>
										at least 20% of the body and 50% of the
										subject.
									</span>
								</p>
							)}
							{!selectedTemplate?.isStale &&
								!staleTemplates?.[`${selectedTemplate?.id}`] &&
								autoSaveStatus === Status.SUCCESS &&
								!isEmpty(unstaledTemplate) && (
									<p className="edit-template-stale-success">
										This is no longer a stale template. You
										are now ready to use it again.
									</p>
								)}
							<div className="saved-loader-group">
								{!selectedTemplate?.isStale &&
									!staleTemplates?.[
										`${selectedTemplate?.id}`
									] &&
									autoSaveStatus === Status.SUCCESS &&
									!isEmpty(unstaledTemplate) && (
										<span className="stale-template-ready">
											<Loader
												className="emailModal-auto-save--loader mr-1.5"
												status={Status.SUCCESS}
											/>
											Ready
										</span>
									)}
								<span
									className={classNames(
										'emailModal-auto-save inline-flex items-center text-base',
										{
											'emailModal-auto-save--visible':
												autoSaveStatus !== Status.NONE
										}
									)}
								>
									<Loader
										className="emailModal-auto-save--loader mr-1.5"
										status={autoSaveStatus}
									/>
									Auto-saved
								</span>
							</div>
						</div>
						{showToaster && (
							<Toaster
								ref={toasterRef}
								message={toasterMessage}
								isVisible={isVisible} // Pass the visibility state
								title="Template Title"
							/>
						)}
						<div className="emailModal-wrapper rounded border">
							<EmailForm
								emailName={selectedTemplate.name ?? ''}
								emailSubject={
									selectedTemplate.subject ?? emailSubject
								}
								emailBody={selectedTemplate.body ?? emailBody}
								onChange={emailOnChange}
								forEditing="true"
								size="lg"
								selectedTemplate={selectedTemplate}
								setSelectedTemplate={setSelectedTemplate}
								validation={{
									subject:
										selectedTemplate &&
										selectedTemplate.subject?.length === 0
											? 'Subject can not be empty.'
											: '',
									body:
										selectedTemplate &&
										selectedTemplate.body?.replace(
											/<[^>]*>?/gm,
											''
										).length === 0
											? 'Body can not be empty.'
											: '',
									name:
										selectedTemplate &&
										selectedTemplate.name?.replace(
											/<[^>]*>?/gm,
											''
										).length === 0
											? 'Template name can not be empty.'
											: ''
								}}
							/>
							{user?.features?.templateRedesign ? (
								<EmailSmartTemplatesRedesign
									selectedTemplate={selectedTemplate}
								/>
							) : (
								<EmailSmartTemplates
									selectedTemplate={selectedTemplate}
								/>
							)}
						</div>
						<div className="info-icon-container">
							<InfoCircle />
						</div>

						{selectedTemplate.body?.includes('[ai_snippet]') && (
							<span className="ai-credit-limit">
								AI credits
								<span
									className={`ai-credit-limit-count ${
										aiCredits !== undefined &&
										aiCredits < list?.businessIds?.length
											? 'insufficient'
											: ''
									} `}
								>
									{aiCredits}
								</span>
								<span
									className={`ai-credit-limit-contacts ${
										list?.id ? 'show' : ''
									}`}
								>
									Contacts in list
								</span>
								<span
									className={`ai-credit-limit-count ${
										list?.id ? 'show' : ''
									}`}
								>
									{list?.businessIds?.length}
								</span>
								<span
									className={`ai-credit-limit-refresh ${
										list?.id ? 'show' : ''
									}`}
								>
									Credit Renewal Date:{' '}
								</span>
								<span
									className={`ai-credit-limit-date ${
										list?.id ? 'show' : ''
									}`}
								>
									{nextMonth}
								</span>
							</span>
						)}
						<div className="ai-credit-limit-hovertext">
							<span className="ai-credit-limit-hovertext-header">
								AI Credits
							</span>
							<span className="ai-credit-limit-hovertext-body">
								Each AI Snippet costs 1 AI credit to generate.
								You may want to regenerate some snippets to your
								taste, so we recommend holding credits in
								reserve. Your next renewal date is listed below.
							</span>
						</div>
						{showAiCreditWarning &&
							selectedTemplate.body?.includes('[ai_snippet]') && (
								<span className="ai-credit-limit-warning">
									<span className="ai-credit-limit-warning-header">
										Warning
									</span>
									<span className="ai-credit-limit-warning-body">
										Your AI credits are insufficient for all
										businesses you're contacting. Some
										emails won't include AI snippets. Switch
										to another template when necessary.
									</span>
									<span
										className="ai-credit-limit-warning-button"
										onClick={(
											e: React.MouseEvent | MouseEvent
										) => {
											e.preventDefault();
											e.stopPropagation();
											setShowAiCreditWarning(false);

											if (
												selectedTemplate.body?.includes(
													'[ai_snippet]'
												) &&
												Object.keys(
													aiText?.[`${list.id}`]?.[
														`${selectedTemplate?.id}`
													] ?? {}
												).length <
													allBusinessEmails?.length &&
												aiCredits > 0 &&
												!aiLoading
											) {
												setShowAiLoadingModal(true);
												rotateTips();
											}

											if (sendAllMode) {
												setShowSendAll(true);
											} else {
												classicMode();
											}
										}}
									>
										Got it
									</span>
									<CloseThin
										className="ai-credit-limit-warning-close"
										onClick={() =>
											setShowAiCreditWarning(false)
										}
									/>
								</span>
							)}
						{showAiTemplateWarning &&
							selectedTemplate.body?.includes('[ai_snippet]') &&
							aiCredits > 0 && (
								<span className="ai-template-change-warning">
									<div>
										<span className="ai-template-change-warning-header">
											Warning
										</span>
										<div className="ai-template-change-warning-body">
											It appears your template has been
											changed. Would you like to revert
											your changes and continue or
											generate new snippets for the
											updated template? Please note that
											generating them will cost you 1 AI
											credit each.
										</div>
									</div>
									<div className="ai-template-change-warning-button-group">
										<span
											className="ai-template-change-warning-continue-button"
											onClick={(
												e: React.MouseEvent | MouseEvent
											) => {
												e.preventDefault();
												e.stopPropagation();
												updateEmail(
													selectedTemplate as Email
												);
												setShowTemplates(false);
												continueToEmailsAnalytic();
												clearUnstaledTemplate();
												clearLastStaledTemplate();
												setShowAiTemplateWarning(false);
												updateEmailTemplate(
													aiTemplates?.[
														`${selectedTemplate?.id}`
													]
												);
												revertAITemplate();

												if (
													autoSaveStatus ===
													Status.SUCCESS
												) {
													setAutoSaveStatus(
														Status.NONE
													);
												}
											}}
										>
											Continue without updating
										</span>
										<span
											className="ai-template-change-warning-regenerate-button"
											onClick={(
												e: React.MouseEvent | MouseEvent
											) => {
												e.preventDefault();
												e.stopPropagation();
												updateEmail(
													selectedTemplate as Email
												);
												setShowTemplates(false);
												continueToEmailsAnalytic();
												clearUnstaledTemplate();
												clearLastStaledTemplate();
												setShowAiTemplateWarning(false);
												deleteAISnippets(
													list.id,
													selectedTemplate.id
												);
												setShowAiLoadingModal(true);
												rotateTips();

												if (
													autoSaveStatus ===
													Status.SUCCESS
												) {
													setAutoSaveStatus(
														Status.NONE
													);
												}
											}}
										>
											Apply changes & regenerate
										</span>
									</div>
									<CloseThin
										className="ai-template-change-warning-close"
										onClick={() =>
											setShowAiTemplateWarning(false)
										}
									/>
								</span>
							)}
					</div>
				);
			} else {
				const sortByPin = (array: EmailTemplate[]) =>
					array?.sort(
						(a, b) =>
							new Date(b.pinDate ?? 0).getTime() -
							new Date(a.pinDate ?? 0).getTime()
					);

				if (emailDebounce.current) {
					clearTimeout(emailDebounce.current);

					emailDebounce.current = undefined;
				}

				title = null;

				modalSize = 'xl';
				bodyClass = 'pt-7 px-4';
				modalHeight = 'full';

				buttons = [];

				content = (
					<div className="select-email-template-holder relative flex h-full flex-grow flex-col pt-4">
						<div className="flex items-center justify-between">
							<span className="text-2xl font-light text-white">
								Select An Email Template
							</span>
							<button
								type="button"
								className="emailModal-create-template-button inline-flex items-center text-lg text-orange"
								onClick={() => setShowCreateAnalytic()}
							>
								<Plus className="mr-3 text-orange" />
								Create template
							</button>
						</div>
						<div className="email-template-search-holder">
							<div className="email-template-search-inner">
								<input
									className="email-template-search-input"
									placeholder="Search for your Template"
									onKeyUp={evt => {
										wordFilterTemplates(
											evt.currentTarget.value
										);
									}}
								/>
								<SearchIconGold className="email-template-search-icon email-template-search-icon-regular" />
							</div>
						</div>

						{templatesList && (
							<EmailTemplateList
								loading={emailTemplatesLoading}
								onSelect={setSelectedTemplate}
								templates={sortByPin(templatesList)}
								onCancel={() => setShowCreate(false)}
								onDelete={openDeleteTemplateModal}
								onDuplicate={(payload: EmailTemplate) =>
									createEmailTemplate(payload)
								}
								onPin={(payload: EmailTemplate) =>
									updateEmailTemplate(payload)
								}
							/>
						)}
					</div>
				);
			}
		} else if (
			selectedTemplate &&
			selectedTemplate.subject &&
			selectedTemplate.subject?.length > 0 &&
			selectedTemplate.body &&
			selectedTemplate.body?.replace(/<[^>]*>?/gm, '').length > 0
		) {
			const canIncreaseEmailLimit =
				user.emailLimit &&
				user.maxEmailLimit &&
				!Number.isNaN(user.emailLimit) &&
				!Number.isNaN(user.maxEmailLimit) &&
				user.emailLimit < user.maxEmailLimit;

			closeIcon = (
				<CloseThin
					width={18}
					height={18}
					className="mb-8 text-white"
					onClick={() => {
						closeEmailModal();
					}}
				/>
			);
			title = (
				<div className="flex flex-col">
					<div>
						<button
							className="emailModal-back emailModal-breadcrumbs-button mr-auto text-base no-underline"
							type="button"
							onClick={() => {
								setShowTemplates(true);
								clearUnstaledTemplate();
								handleClearFilters();
							}}
						>
							Back to template
						</button>
					</div>
					<div className="mt-2 flex">
						<span className="emailModal-list-view-header flex flex-col items-center">
							<span className="text-2xl font-light text-white">
								{!directEmail && (
									<>
										Page{' '}
										{`${(appliedFilters?.skip || 0) / (appliedFilters?.limit || 50) + 1} `}
										from list &ldquo;
										<span className="emailModal-list-view-header-list-name">
											{list.name}
										</span>
										&rdquo;
										<br />
										<span className="list-sub-header">
											(Only businesses with emails appear)
										</span>
									</>
								)}
								{directEmail && 'Send direct email'}
							</span>
						</span>
						{selectedTemplate.subject.length > 0 &&
							user.role !== UserRole.PreTrial &&
							selectedTemplate.body.replace(/<[^>]*>?/gm, '')
								.length > 0 && (
								<div className="emailModal-limit-queue-div items-enter -mr-4 ml-auto inline-flex">
									<span className="align-center flex">
										{scheduledEmailActivity[
											`${activeBusiness?._id}`
										] ? (
											<button
												id="cancel-scheduled-button"
												className="scheduled-send-cancel-button"
												onClick={
													openScheduledSendCancelModal
												}
											>
												<TrashFullSquare className="scheduled-send-cancel-icon" />
												Cancel scheduled
											</button>
										) : (
											<span
												className={`emailModal-limit emailModal-limit--left font-light${
													emailFailure ===
													'Error: Email limit reached'
														? 'emailModal-no-emails'
														: ''
												}`}
											>
												{(user.emailLimitLeft as number) <
												0
													? 0
													: user.emailLimitLeft}{' '}
												emails left today
											</span>
										)}
									</span>
									{!isEmpty(emailQueue) && (
										<span className="emailModal-queue">
											<span className="emailModal-queue-bolded">
												{Object.keys(emailQueue).length}{' '}
												{Object.keys(emailQueue)
													.length === 1
													? 'email'
													: 'emails'}
											</span>
											queued to send
											<InfoGold className="emailModal-queue-icon" />
											<span className="emailModal-queue-hovertext">
												Resquared now waits 30 seconds
												between sending each email so
												your outreach is less likely to
												end up in spam.
											</span>
										</span>
									)}
								</div>
							)}
					</div>
				</div>
			);

			buttons = [];
			bodyClass = 'email-modal-background';
			modalSize = 'xl';
			modalHeight = 'full';
			content =
				businessEmails.length > 0 ? (
					<EmailList
						onSelect={b => setActiveBusiness(b)}
						activeBusiness={activeBusiness}
						onHide={id => setRemovedEmails([...removedEmails, id])}
						data={businessEmails}
						emailData={{
							subject: selectedTemplate.subject,
							body: selectedTemplate.body,
							templateId: selectedTemplate.id
						}}
						directEmail={directEmail}
						user={user}
						selectedTemplate={selectedTemplate}
						setSelectedTemplate={setSelectedTemplate}
						linkedInSharer={linkedInSharer}
						setDeleteDataIndex={setDeleteDataIndex}
						setBusinesses={setBusinessEmails}
						setShowAiLoadingModal={setShowAiLoadingModal}
						rotateTips={rotateTips}
						appliedFilters={appliedFilters}
						onFilterChange={filters => {
							onFilterChange(filters);
							getBusinesses(filters);
						}}
					/>
				) : (
					<EmptyMessage
						title="No businesses in this list have emails!"
						message="Use Filters to help ensure that businesses you want to email have appropriate contact information."
					/>
				);
		}
	}

	return (
		<>
			{showSendAll && (
				<SendAllModal
					activeBusiness={activeBusiness}
					setActiveBusiness={setActiveBusiness}
					selectedTemplate={selectedTemplate}
					setDeleteDataIndex={setDeleteDataIndex}
					openScheduledSendCancelModal={openScheduledSendCancelModal}
					setShowSendAll={setShowSendAll}
					closeEmailModal={closeEmailModal}
					setCustomEmailModalDisplay={setCustomEmailModalDisplay}
					sendAllMode={sendAllMode}
					setSendAllMode={setSendAllMode}
					classicMode={classicMode}
					showAiLoadingModal={showAiLoadingModal}
					appliedFilters={appliedFilters}
				/>
			)}
			<Modal
				title={title && <div>{title}</div>}
				titleClass="emailModal-title"
				content={content}
				buttons={buttons}
				onClose={resetClose}
				show={show || showPreview}
				size={modalSize}
				height={modalHeight}
				bodyClass={
					!showTemplates && !directEmail
						? bodyClass
						: 'custom-modal-content'
				} // Conditional bodyClass based on multiple conditions. This is done in order to style header with X on edit page				closeIcon={closeIcon}
				setShowSendAll={setShowSendAll}
				sendAllMode={sendAllMode}
				setSendAllMode={setSendAllMode}
				backToTemplateEdit={backToTemplateEdit}
				customEmailModalDisplay={customEmailModalDisplay}
				setCustomEmailModalDisplay={setCustomEmailModalDisplay}
				showTemplates={showTemplates}
				directEmail={directEmail}
			/>

			<div className="ai-failure-outer">
				<div className="ai-failure-background" />
				<div className="ai-failure-container">
					<span className="ai-failure-content">
						AI generation failed. Please try again.
					</span>
					<div
						className="ai-failure-close-button"
						onClick={() => {
							closeAiFailureModal();
						}}
					>
						Got it
					</div>
				</div>
			</div>
			{showAiLoadingModal && (
				<div className="ai-loading">
					<div className="ai-loading-background" />
					<div className="ai-loading-inner">
						<div className="ai-loading-inner-left">
							<MapPin className="ai-loading-inner-left-icon" />
							<img src={map} alt="Map" />
							<div className="ai-loading-inner-left-tip">
								<h1 className="ai-loading-inner-left-tip-header">
									Re2 Tips
								</h1>
								<div className="ai-loading-inner-left-tip-divider" />
								<p className="ai-loading-inner-left-tip-text">
									{currentAiLoadingTip}
								</p>
							</div>
						</div>
						<div className="ai-loading-inner-right">
							<div className="ai-loading-inner-right-info">
								<div className="ai-loading-inner-right-info-left">
									<Coin />
									AI Credits
									<Hashtag
										className={`ai-loading-inner-right-info-left-hashtag-icon ${
											list?.id ? 'show' : ''
										}`}
									/>
									<span
										className={`ai-loading-inner-right-info-left-businesses ${
											list?.id ? 'show' : ''
										}`}
									>
										Businesses
									</span>
									<Renewal
										className={`ai-loading-inner-right-info-left-refresh-icon ${
											list?.id ? 'show' : ''
										}`}
									/>
									<span
										className={`ai-loading-inner-right-info-left-businesses ${
											list?.id ? 'show' : ''
										}`}
									>
										Credit renewal
									</span>
								</div>
								<div className="ai-loading-inner-right-info-right">
									<span className="ai-loading-inner-right-info-right-credits">
										{aiCredits}
									</span>
									<span className="ai-loading-inner-right-info-right-businesses">
										{list?.businessIds?.length}
									</span>
									<span className="ai-loading-inner-right-info-left-refresh">
										{nextMonth}
									</span>
								</div>
							</div>
							<div className="ai-loading-inner-right-loading">
								<LogoText className="ai-loading-inner-right-loading-header" />
								<span className="ai-loading-inner-right-loading-content">
									Generating AI Snippets
									<div className="ai-loading-inner-right-loading-content-ellipsis ml-2" />
									<div className="ai-loading-inner-right-loading-content-ellipsis" />
									<div className="ai-loading-inner-right-loading-content-ellipsis" />
									<div className="ai-loading-inner-right-loading-content-ellipsis" />
									<div className="ai-loading-inner-right-loading-content-ellipsis" />
									<div className="ai-loading-inner-right-loading-content-ellipsis" />
									<FourCircles className="ai-loading-inner-right-loading-content-icon" />
								</span>
							</div>
						</div>
					</div>
				</div>
			)}
			<div
				className="delete-template-modal delete-template-modal-closed delete-template-modal-fade"
				id="delete-template-modal"
			>
				<div
					className="delete-template-modal-background"
					onClick={() => (
						setDeleteTemplateId(null),
						closeModal('delete-template-modal')
					)}
				/>
				<div className="delete-template-inner-modal">
					<div className="delete-template-modal-title">
						Are you sure you want to delete this template? &nbsp;
						<p>This action cannot be undone.</p>
					</div>
					<span className="delete-template-button-holder">
						<button
							className="delete-template-secondary"
							onClick={() => (
								setDeleteTemplateId(null),
								closeModal('delete-template-modal')
							)}
						>
							Cancel
						</button>
						<button
							className="delete-template-primary"
							onClick={() => {
								deleteEmailTemplate(deleteTemplateId);
								setDeleteTemplateId(null);
								closeModal('delete-template-modal');
							}}
						>
							<p>Delete permanently</p>
						</button>
					</span>
				</div>
			</div>

			<div
				className="scheduled-send-cancel-modal scheduled-send-cancel-modal-closed scheduled-send-cancel-modal-fade"
				id="scheduled-send-cancel-modal"
			>
				<div className="scheduled-send-cancel-modal-background" />
				<div className="scheduled-send-cancel-inner-modal">
					<div className="scheduled-send-cancel-modal-title">
						Are you sure you want to cancel your scheduled email?
					</div>
					{cancelScheduledEmailFailure && (
						<div className="scheduled-send-cancel-failure">
							Something went wrong, please try again.
						</div>
					)}
					<span className="scheduled-send-cancel-button-holder">
						{!cancelScheduledEmailSuccess && (
							<button
								className="scheduled-send-cancel-secondary"
								onClick={closeScheduledSendCancelModal}
							>
								Close
							</button>
						)}
						<button
							id="scheduled-send-inner-cancel"
							className={`scheduled-send-cancel-primary ${
								cancelScheduledEmailSuccess ? 'success' : ''
							}`}
							disabled={cancelScheduledEmailSuccess}
							onClick={() => {
								cancelScheduledEmail(
									showPreview
										? business?._id
										: activeBusiness?._id,
									list?.id,
									scheduledEmailActivity[
										`${
											showPreview
												? business?._id
												: activeBusiness?._id
										}`
									]?.requestedScheduleDate
								);
							}}
						>
							{cancelScheduledEmailButtonText()}
							{cancelScheduledEmailLoading && (
								<Loader
									className="scheduled-send-cancel-primary-loader"
									status={Status.LOADING}
								/>
							)}
							{cancelScheduledEmailSuccess && (
								<CheckMarkGreen className="scheduled-send-cancel-primary-checkmark" />
							)}
						</button>
					</span>
				</div>
			</div>

			<div
				className="stale-template-modal stale-template-modal-closed stale-template-modal-fade"
				id="stale-template-modal"
			>
				<div
					className="stale-template-modal-background"
					onClick={() => closeModal('stale-template-modal')}
				/>
				<div className="stale-template-modal-inner">
					<CloseThin
						className="stale-template-modal-close-icon"
						onClick={() => closeModal('stale-template-modal')}
					/>
					<div className="stale-template-modal-text">
						This email template has been used for 500 times. <br />
						Please select a different template, or change the
						current template.
					</div>
					<div className="stale-template-modal-button-holder">
						<button
							className="stale-template-modal-button-select"
							onClick={() => {
								setShowTemplates(true);
								setSelectedTemplate(undefined);
								setShowCreate(false);
								closeModal('stale-template-modal');
							}}
						>
							Select template
						</button>
						<button
							className="stale-template-modal-button-edit"
							onClick={() => {
								setShowTemplates(true);
								closeModal('stale-template-modal');
							}}
						>
							Edit template
						</button>
					</div>
				</div>
			</div>
		</>
	);
}
